import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import {Button, ColorInput} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {getModelTypeConstantForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {assert, assertAndContinue} from 'Asserts';
import DropdownSelector from 'insights/analytics-visualizations/configs/form-components/DropdownSelector';
import {GetPipelineConfigSpecType, PipelineConfigJsonSpec, isStringSpec} from 'pipelines/StepShapeUtils';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import TextArea from 'ui-library/bs/TextArea';
import {ButtonSize} from 'ui-library/buttons/Button';
import FieldError from 'ui-library/FieldError';
import BenchlingIcon, {isValidBenchlingIcon} from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {IntermediateValueControlledNumericInput} from 'ui-library/inputs/ControlledNumericInput';
import TextInput from 'ui-library/inputs/TextInput';
import MarkdownContent from 'ui-library/MarkdownContent';
import {JSONValue} from 'util/Types';

import IconEnumSelector from '../IconEnumSelector';
import {isEditorDisabled} from '../step-config-settings/StepConfigDisableFieldFunctions';
import {
  getConfigShapeAtConfigShapePath,
  maybeGetValueFromConfig,
} from '../step-config-settings/StepConfigSettingsUtils';
import {useStepConfigEditorContext} from '../StepConfigEditorContext';
import {
  EnumUISchema,
  StepConfigEditorOverride,
  StepConfigUISchemaV2,
  ConfigShapePath,
  isShapeUISchemaV2,
  assertTypeValidForEditorOverride,
  TaggedUnionUISchema,
  CONDITIONAL_UI_SCHEMA_KEY,
  isCheckboxFieldSettings,
  isColumnReferenceFieldSettings,
  CUSTOM_COMPONENT_KEY,
  isFieldUISchemaWithCustomComponentV2,
  isColumnValueFieldSettings,
  isTableReferenceFieldSettings,
  isArrayFieldSettings,
  isIconSelectorFieldSettings,
  isStringFieldSettings,
  isFieldUISchemaActionButton,
  FIELD_ORDER_UI_SCHEMA_KEY,
  ShapeUISchemaV2,
  isMappingArrayFieldSettings,
  FieldUISchemaWithStandardComponentV2,
  ConfigDataFrameInfo,
  isFieldUISchemaMarkdown,
} from '../StepConfigEditorTypes';
import ArrayEditorV2 from './ArrayEditorV2';
import ColumnReferenceEditor from './ColumnReferenceEditor';
import DataFrameColumnValueEditor from './DataFrameColumnValueEditor';
import EnumEditor from './EnumEditor';
import QueryingMappingArrayEditor from './MappingArrayEditor';
import MultiSelectColumnReferenceEditor from './MultiSelectColumnReferenceEditor';
import SearchEditor from './SearchEditor';
import ShapeEditorV2 from './ShapeEditorV2';
import {BOOLEAN_EDITOR_OPTIONS} from './StepConfigEditorConstants';
import {getMinMaxErrorMessage} from './StepConfigEditorRouter';
import {VariableHeaderOrNull} from './StepConfigFieldsTemplate';
import TableReferenceEditor from './TableReferenceEditor';
import {TagChoiceDropdown} from './TaggedUnionEditor';

export interface StepConfigEditorRouterV2Props {
  config: Record<string, JSONValue>;
  configShape: PipelineConfigJsonSpec;
  dataTestAttrs?: Pick<DataTestAttrs, 'key' | 'index'>;
  onChangeConfig: (config: JSONValue, path: ConfigShapePath, newValue: JSONValue) => void;
  uiSchema: StepConfigUISchemaV2;
  disabled?: boolean;
  isExpandable?: boolean;
  isExpanded?: boolean;
}

interface FieldEditorProps extends Omit<StepConfigEditorRouterV2Props, 'uiSchema'> {
  dataFramesInfos: Record<string, Array<ConfigDataFrameInfo>>;
  uiSchema: FieldUISchemaWithStandardComponentV2;
}

function FieldEditor({
  dataFramesInfos,
  config,
  onChangeConfig,
  configShape,
  uiSchema,
  disabled,
  dataTestAttrs = {},
  isExpandable,
  isExpanded,
}: FieldEditorProps): JSX.Element {
  const columns = Object.values(dataFramesInfos)
    .flatMap((dataFrameInfos) => dataFrameInfos)
    .flatMap(({dataFrameSpec: {columns}}) => columns);
  const shouldDisableEditor = isEditorDisabled({uiSchema, config, columns}) || disabled;
  const currentConfigShape = getConfigShapeAtConfigShapePath(uiSchema.configShapePath, configShape, config);
  if (uiSchema.editorOverride) {
    assertTypeValidForEditorOverride(uiSchema.editorOverride, currentConfigShape);
  }
  switch (currentConfigShape.kind) {
    case 'string': {
      if (uiSchema.editorOverride === StepConfigEditorOverride.STRING_LABEL) {
        return (
          <div className={css(styles.oneRowWrapper, styles.stringLabelWrapper)}>
            <VariableHeaderOrNull
              variableName={
                (maybeGetValueFromConfig(config, uiSchema.configShapePath) as string | null) ?? ''
              }
              isCompact={true}
            />
          </div>
        );
      }
      if (uiSchema.editorOverride === StepConfigEditorOverride.COLOR_PICKER) {
        return (
          <div className={css(styles.oneRowWrapper, styles.grow)}>
            <ColorInput
              value={(maybeGetValueFromConfig(config, uiSchema.configShapePath) as string | null) ?? ''}
              onChange={(value: string) => {
                onChangeConfig(config, uiSchema.configShapePath, value);
              }}
              swatchesPerRow={8}
              disabled={shouldDisableEditor}
              className={css(styles.colorInput)}
            />
          </div>
        );
      }

      let useTextArea = false;
      if (uiSchema.hasOwnProperty('fieldSettings')) {
        assert(isStringFieldSettings(uiSchema.fieldSettings), 'Invalid field settings for string field');
        useTextArea = uiSchema.fieldSettings?.useTextArea ?? false;
      }

      if (useTextArea) {
        return (
          <div className={css(styles.oneRowWrapper)}>
            <div className={css(styles.grow)}>
              <TextArea
                className={css(styles.textArea)}
                dataTestAttrs={{component: 'StepConfigEditor', element: 'textArea', ...dataTestAttrs}}
                value={(maybeGetValueFromConfig(config, uiSchema.configShapePath) as string | null) ?? ''}
                onChange={(e) => {
                  onChangeConfig(config, uiSchema.configShapePath, e.target.value);
                }}
                disabled={shouldDisableEditor}
              />
            </div>
          </div>
        );
      } else {
        return (
          <div className={css(styles.oneRowWrapper)}>
            <TextInput
              className={css(styles.primitiveEditor)}
              rootClassName={css(styles.grow)}
              dataTestAttrs={{component: 'StepConfigEditor', element: 'textInput', ...dataTestAttrs}}
              value={(maybeGetValueFromConfig(config, uiSchema.configShapePath) as string | null) ?? ''}
              placeholder="Text"
              onChange={(e) => {
                onChangeConfig(config, uiSchema.configShapePath, e.target.value);
              }}
              disabled={shouldDisableEditor}
            />
          </div>
        );
      }
    }
    case 'boolean':
      if (uiSchema.editorOverride === StepConfigEditorOverride.CHECKBOX) {
        assert(
          isCheckboxFieldSettings(uiSchema.fieldSettings),
          'Checkbox label is required for checkbox editor override'
        );
        const checkboxLabel = uiSchema.fieldSettings.checkboxLabel;
        return (
          <div className={css(styles.oneRowWrapper)}>
            <CheckboxInput
              dataTestAttrs={{component: 'StepConfigEditor', element: 'checkbox', ...dataTestAttrs}}
              checked={maybeGetValueFromConfig(config, uiSchema.configShapePath) as boolean | false}
              onChange={(e) => {
                onChangeConfig(config, uiSchema.configShapePath, e.target.checked);
              }}
              disabled={shouldDisableEditor}
              id={`${uiSchema.configShapePath.join('.')}-${dataTestAttrs?.index ? dataTestAttrs.index : ''}`}
              label={checkboxLabel}
            />
          </div>
        );
      }
      return (
        <div className={css(styles.oneRowWrapper)}>
          <div className={css(styles.grow)}>
            <DropdownSelector<boolean>
              dataTestAttrs={{component: 'StepConfigEditor', element: 'dropdown', ...dataTestAttrs}}
              selectedValue={maybeGetValueFromConfig(config, uiSchema.configShapePath) as boolean | null}
              onChange={(selectedValue) => {
                onChangeConfig(config, uiSchema.configShapePath, selectedValue);
              }}
              allowNull={!currentConfigShape.required}
              buttonSize={ButtonSize.SMALL}
              searchable={false}
              optionDatas={BOOLEAN_EDITOR_OPTIONS}
              disabled={shouldDisableEditor}
            />
          </div>
        </div>
      );
    case 'number':
      return (
        <div className={css(styles.oneRowWrapper)}>
          <IntermediateValueControlledNumericInput
            dataTestAttrs={{component: 'StepConfigEditor', element: 'numericInput', ...dataTestAttrs}}
            onChange={(e) => {
              onChangeConfig(config, uiSchema.configShapePath, e.target.numericValue);
            }}
            value={maybeGetValueFromConfig(config, uiSchema.configShapePath) as number | null}
            placeholder="Number"
            className={css(styles.primitiveEditor)}
            isDisabled={shouldDisableEditor}
          />
        </div>
      );
    case 'integer':
      return (
        <>
          <div className={css(styles.oneRowWrapper)}>
            <IntermediateValueControlledNumericInput
              dataTestAttrs={{component: 'StepConfigEditor', element: 'numericInput', ...dataTestAttrs}}
              onChange={(e) => {
                onChangeConfig(
                  config,
                  uiSchema.configShapePath,
                  e.target.numericValue ?? (e.target.value || null)
                );
              }}
              value={maybeGetValueFromConfig(config, uiSchema.configShapePath) as number | null}
              placeholder="Integer"
              className={css(styles.primitiveEditor)}
              validateInteger={true}
              min={currentConfigShape.minValue}
              max={currentConfigShape.maxValue}
              isDisabled={shouldDisableEditor}
            />
          </div>
          {getMinMaxErrorMessage(config, currentConfigShape) && (
            <FieldError message={getMinMaxErrorMessage(config, currentConfigShape)} />
          )}
        </>
      );
    case 'enum': {
      const enumUISchema = {...uiSchema, ...uiSchema.fieldSettings};

      if (uiSchema.editorOverride === StepConfigEditorOverride.ICON_SELECTOR) {
        assert(
          isIconSelectorFieldSettings(uiSchema.fieldSettings),
          'Icon selector fieldSettings is required for icon selector editor override'
        );
        return (
          <div className={css(styles.oneRowWrapper, styles.grow)}>
            <IconEnumSelector
              dataTestAttrs={{component: 'StepConfigEditor', element: 'iconSelector', ...dataTestAttrs}}
              selectedOption={
                maybeGetValueFromConfig(config, uiSchema.configShapePath) as string | number | null
              }
              configShape={currentConfigShape}
              onChangeConfig={(newValue) => {
                onChangeConfig(config, uiSchema.configShapePath, newValue);
              }}
              fieldSettings={uiSchema.fieldSettings}
              disabled={shouldDisableEditor}
              isExpandable={isExpandable}
              isExpanded={isExpanded}
            />
          </div>
        );
      }
      return (
        <div className={css(styles.oneRowWrapper, styles.grow)}>
          <EnumEditor
            dataTestAttrs={{component: 'StepConfigEditor', element: 'dropdown', ...dataTestAttrs}}
            config={maybeGetValueFromConfig(config, uiSchema.configShapePath) as string | number | null}
            configShape={currentConfigShape}
            onChangeConfig={(newValue) => {
              onChangeConfig(config, uiSchema.configShapePath, newValue);
            }}
            uiSchema={enumUISchema as EnumUISchema | undefined}
            disabled={shouldDisableEditor}
            buttonSize={ButtonSize.SMALL}
            isExpandable={isExpandable}
            isExpanded={isExpanded}
          />
        </div>
      );
    }
    case 'taggedUnion': {
      const tagKey = currentConfigShape.tagKey;
      const taggedUnionUISchema = {...uiSchema, ...uiSchema.fieldSettings};
      return (
        <TagChoiceDropdown
          selectedTag={
            maybeGetValueFromConfig(config, [...uiSchema.configShapePath, tagKey]) as string | null
          }
          onChangeChoice={(choiceShape: GetPipelineConfigSpecType<'shape'>) => {
            const tagValueShape = choiceShape.fields[tagKey];
            assertAndContinue(isStringSpec(tagValueShape), 'Tag value must be a string', {
              kind: tagValueShape.kind,
            });
            // TODO(BNCH-119690): Update config to have fields that are in the new tagged union shape
            onChangeConfig(
              config,
              [...uiSchema.configShapePath, tagKey],
              // Typescript does not understand the previous assert
              (tagValueShape as Extract<PipelineConfigJsonSpec, {kind: 'string'}>).value as string
            );
          }}
          uiSchema={taggedUnionUISchema as TaggedUnionUISchema | undefined}
          configShape={currentConfigShape}
          disabled={shouldDisableEditor}
          buttonSize={ButtonSize.SMALL}
        />
      );
    }
    case 'columnReference': {
      let noColumnSelectedText;
      let allowSelectNull;
      let filterOptions;
      let renderCyclingSelector;
      if (uiSchema.hasOwnProperty('fieldSettings')) {
        assert(
          isColumnReferenceFieldSettings(uiSchema.fieldSettings),
          'columnReference fieldSettings must be type ColumnReferenceFieldSettings'
        );
        noColumnSelectedText = uiSchema.fieldSettings?.noColumnSelectedText;
        allowSelectNull = uiSchema.fieldSettings?.allowSelectNull;
        filterOptions = uiSchema.fieldSettings?.systemNameWildcardSelector
          ? {systemNameWildcardSelector: uiSchema.fieldSettings.systemNameWildcardSelector}
          : undefined;
        renderCyclingSelector = uiSchema.fieldSettings?.renderCyclingSelector ?? false;
      }
      return (
        <div className={css(styles.oneRowWrapper)}>
          <div className={css(styles.grow)}>
            <ColumnReferenceEditor
              config={maybeGetValueFromConfig(config, uiSchema.configShapePath) as string | null}
              configShape={currentConfigShape}
              onChangeConfig={(newValue) => {
                onChangeConfig(config, uiSchema.configShapePath, newValue);
              }}
              dataTestAttrs={{component: 'StepConfigEditor', element: 'dropdown', ...dataTestAttrs}}
              disabled={shouldDisableEditor}
              buttonSize={ButtonSize.SMALL}
              noColumnSelectedText={noColumnSelectedText}
              allowSelectNull={allowSelectNull}
              filterOptions={filterOptions}
              renderCyclingSelector={renderCyclingSelector}
            />
          </div>
        </div>
      );
    }
    case 'columnValue': {
      let noValueSelectedText;
      let renderCyclingSelector;
      if (uiSchema.fieldSettings != null) {
        assert(
          isColumnValueFieldSettings(uiSchema.fieldSettings),
          'columnValue fieldSettings must be type ColumnValueFieldSettings'
        );
        noValueSelectedText = uiSchema.fieldSettings.noValueSelectedText;
        renderCyclingSelector = uiSchema.fieldSettings?.renderCyclingSelector ?? false;
      }
      return (
        <div className={css(styles.oneRowWrapper)}>
          <div className={css(styles.grow)}>
            <DataFrameColumnValueEditor
              dataTestAttrs={{component: 'StepConfigEditor', element: 'dropdown', ...dataTestAttrs}}
              config={maybeGetValueFromConfig(config, uiSchema.configShapePath) as string | null}
              configShape={currentConfigShape}
              onChangeConfig={(newValue) => {
                onChangeConfig(config, uiSchema.configShapePath, newValue);
              }}
              disabled={shouldDisableEditor}
              buttonSize={ButtonSize.SMALL}
              noValueSelectedText={noValueSelectedText}
              renderCyclingSelector={renderCyclingSelector}
            />
          </div>
        </div>
      );
    }
    case 'tableReference': {
      let disabledColumn;
      let hiddenColumn;
      if (uiSchema.fieldSettings != null) {
        assert(
          isTableReferenceFieldSettings(uiSchema.fieldSettings),
          'tableReference fieldSettings must be type TableReferenceFieldSettings'
        );
        disabledColumn = uiSchema.fieldSettings.disabledColumn;
        hiddenColumn = uiSchema.fieldSettings.hiddenColumn;
      }
      return (
        <TableReferenceEditor
          config={
            maybeGetValueFromConfig(config, uiSchema.configShapePath) as Array<Record<string, JSONValue>>
          }
          configShape={currentConfigShape}
          onChangeConfig={(newValue) => {
            onChangeConfig(config, uiSchema.configShapePath, newValue);
          }}
          dataTestAttrs={{component: 'StepConfigEditor', element: 'table', ...dataTestAttrs}}
          disabled={shouldDisableEditor}
          disabledColumns={disabledColumn}
          hiddenColumns={hiddenColumn}
        />
      );
    }
    case 'domain': {
      return (
        <div className={css(styles.oneRowWrapper)}>
          <div className={css(styles.grow)}>
            <SearchEditor
              config={maybeGetValueFromConfig(config, uiSchema.configShapePath) as string | null}
              onChangeConfig={(newValue) => {
                onChangeConfig(config, uiSchema.configShapePath, newValue);
              }}
              disabled={shouldDisableEditor}
              modelTypes={currentConfigShape.types.map((type) => getModelTypeConstantForObjectType(type))}
            />
          </div>
        </div>
      );
    }
    case 'array': {
      if (uiSchema.editorOverride === StepConfigEditorOverride.MAPPING_ARRAY) {
        assert(
          isMappingArrayFieldSettings(uiSchema.fieldSettings),
          'MAPPING_ARRAY editorOverride requires MappingArrayFieldSettings'
        );
        const mappingSettings = uiSchema.fieldSettings;
        return (
          <div className={css(styles.oneRowWrapper)}>
            <div className={css(styles.grow)}>
              <QueryingMappingArrayEditor
                config={config}
                configShape={currentConfigShape}
                configShapePath={uiSchema.configShapePath}
                onChangeConfig={onChangeConfig}
                mappingSettings={mappingSettings}
                elementUISchema={uiSchema.elementUISchema}
                disabled={shouldDisableEditor}
              />
            </div>
          </div>
        );
      }

      if (currentConfigShape.elementSpec.kind === 'columnReference') {
        return (
          <div className={css(styles.oneRowWrapper)}>
            <div className={css(styles.grow)}>
              <MultiSelectColumnReferenceEditor
                config={
                  (maybeGetValueFromConfig(config, uiSchema.configShapePath) as Array<string | null>) ?? []
                }
                configShape={currentConfigShape}
                dataTestAttrs={{
                  component: 'StepConfigEditor',
                  element: 'multiSelectDropdown',
                  ...dataTestAttrs,
                }}
                onChangeConfig={(newValue) => {
                  onChangeConfig(config, uiSchema.configShapePath, newValue);
                }}
                disabled={disabled}
              />
            </div>
          </div>
        );
      }

      let disableAddRemove;
      if (uiSchema.fieldSettings != null) {
        assert(
          isArrayFieldSettings(uiSchema.fieldSettings),
          'array fieldSettings must be type ArrayFieldSettings'
        );
        disableAddRemove = uiSchema.fieldSettings.disableAddRemove;
      }
      return (
        <div className={css(styles.oneRowWrapper)}>
          <div className={css(styles.grow)}>
            <ArrayEditorV2
              config={config}
              configShape={currentConfigShape}
              dataTestAttrs={dataTestAttrs}
              onChangeConfig={onChangeConfig}
              elementUISchema={uiSchema.elementUISchema}
              disabled={shouldDisableEditor}
              configShapePath={uiSchema.configShapePath}
              disableAddRemove={disableAddRemove}
            />
          </div>
        </div>
      );
    }
    case 'shape': {
      // Auto-generate UISchema for shapes that don't have one.
      // This commonly happens when ArrayEditorV2 wraps scalar elements
      // in temporary shapes, but also serves as a fallback for any shape
      // rendered without explicit UI configuration.
      const fields = Object.keys(currentConfigShape.fields);
      const shapeUISchema: ShapeUISchemaV2 = {
        [FIELD_ORDER_UI_SCHEMA_KEY]: fields,
        ...Object.fromEntries(
          fields.map((fieldName) => [
            fieldName,
            {
              displayName: fieldName,
              configShapePath: [...uiSchema.configShapePath, fieldName],
            },
          ])
        ),
      };

      return (
        <ShapeEditorV2
          configShape={currentConfigShape}
          config={config}
          onChangeConfig={onChangeConfig}
          uiSchema={shapeUISchema}
          disabled={disabled}
        />
      );
    }
    default:
      assertAndContinue(false, 'Unsupported config shape', {kind: currentConfigShape.kind});
      return <div>Unsupported</div>;
  }
}

/**
 * Render the correct editor based on the `uiSchema` and `configShape`.
 *
 * The `uiSchema` is the structure of the editor, and the `configShape`. The router recursively renders
 * editors for each field in the `uiSchema`, rendering the correct editor based on its associated field
 * in configShape.
 *
 */
export default function StepConfigEditorRouterV2({
  config,
  onChangeConfig,
  configShape,
  uiSchema,
  disabled,
  dataTestAttrs = {},
  isExpandable,
  isExpanded,
}: StepConfigEditorRouterV2Props): JSX.Element {
  const {dataFramesInfos, actions} = useStepConfigEditorContext();
  if (isShapeUISchemaV2(uiSchema)) {
    if (uiSchema.hasOwnProperty(CONDITIONAL_UI_SCHEMA_KEY) && uiSchema[CONDITIONAL_UI_SCHEMA_KEY] != null) {
      const tagValueInConfig = maybeGetValueFromConfig(
        config,
        uiSchema[CONDITIONAL_UI_SCHEMA_KEY].configShapePath
      );
      if (tagValueInConfig !== uiSchema[CONDITIONAL_UI_SCHEMA_KEY].tagValue) {
        return <></>;
      }
    }
    return (
      <ShapeEditorV2
        configShape={configShape}
        config={config}
        onChangeConfig={onChangeConfig}
        uiSchema={uiSchema}
        disabled={disabled}
      />
    );
  }

  if (isFieldUISchemaWithCustomComponentV2(uiSchema)) {
    return uiSchema[CUSTOM_COMPONENT_KEY]({
      configShape,
      config,
      onChangeConfig,
      disabled,
    });
  }

  if (isFieldUISchemaActionButton(uiSchema)) {
    const action = actions?.[uiSchema.key];
    return (
      <div
        className={css(styles.actionButtonWrapper)}
        {...getTestProps({
          component: 'StepConfigEditor',
          element: 'button',
          key: uiSchema.key,
          ...dataTestAttrs,
        })}
      >
        <Button
          leftIcon={
            uiSchema.buttonIcon != null && isValidBenchlingIcon(uiSchema.buttonIcon) ? (
              <BenchlingIcon icon={uiSchema.buttonIcon} />
            ) : null
          }
          onClick={action != null ? action : noop}
          variant={uiSchema.buttonVariant}
          disabled={action == null}
        >
          {uiSchema.buttonText}
        </Button>
        {uiSchema.helpText}
      </div>
    );
  }

  if (isFieldUISchemaMarkdown(uiSchema)) {
    return (
      <div className={css(styles.oneRowWrapper)}>
        <MarkdownContent content={uiSchema.markdown} />
      </div>
    );
  }

  return (
    <FieldEditor
      dataFramesInfos={dataFramesInfos}
      configShape={configShape}
      config={config}
      onChangeConfig={onChangeConfig}
      uiSchema={uiSchema}
      dataTestAttrs={dataTestAttrs}
      disabled={disabled}
      isExpandable={isExpandable}
      isExpanded={isExpanded}
    />
  );
}

const styles = StyleSheet.create({
  textArea: {
    resize: 'vertical',
  },
  grow: {
    flexGrow: 1,
  },
  oneRowWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 8,
  },
  primitiveEditor: {
    ...FONT_PROXIMA_13,
    borderRadius: 4,
  },
  actionButtonWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  colorInput: {
    width: '100%',
  },
  stringLabelWrapper: {
    marginBottom: '-8px',
  },
});
